import { sectorsFor, type Coord, type Sector } from '../data/communications'
import { BROSSARD_DISTRICTS } from '../data/brossard'
import type { Localized } from '../types/notice'
import type { City } from './activeCity'

export interface CityDistrict {
  id: string
  /** Short text shown in the map badge ("3", "A-B-E"). */
  badge: string
  label: Localized
  /** Sectors the district can see — for Brossard, any sector partly inside it. */
  sectors: Sector[]
  /**
   * Fraction of each sector (by sector id) lying inside the district. Missing
   * when every sector belongs wholly to the district.
   */
  shares?: Record<string, number>
  /** The district's own boundary, when it differs from its sectors' union. */
  polygon?: Coord[]
  /** Where to place the number badge. */
  badgeAt?: Coord
}

/** Sector letter(s) from its label, e.g. "Secteur L" → "L". */
export function sectorLetter(sector: Sector) {
  return sector.label.fr.replace(/^Secteurs? /, '')
}

/** Fraction of a sector lying inside a district (1 when not split). */
export function sectorShare(d: CityDistrict, sectorId: string) {
  return d.shares?.[sectorId] ?? 1
}

/**
 * Political districts of a city. Brossard has 12 districts grouping its
 * sectors; cities without districts get one "district" per sector.
 */
export function districtsFor(city: City): CityDistrict[] {
  const sectors = sectorsFor(city)
  if (city === 'Brossard') {
    return BROSSARD_DISTRICTS.map((d) => ({
      id: d.id,
      badge: String(d.number),
      label: { fr: `District ${d.number}`, en: `District ${d.number}` },
      sectors: d.letters
        .map((l) => sectors.find((s) => s.id === `br-${l.toLowerCase()}`))
        .filter((s): s is Sector => Boolean(s)),
      shares: Object.fromEntries(
        d.letters.map((l) => [`br-${l.toLowerCase()}`, d.shares[l]]),
      ),
      polygon: d.polygon,
      badgeAt: d.badge,
    }))
  }
  return sectors.map((s) => ({
    id: s.id,
    badge: sectorLetter(s),
    label: s.label,
    sectors: [s],
  }))
}

/** Every vertex of the district, for fitting the map to it. */
export function districtPoints(d: CityDistrict): Coord[] {
  return d.polygon ?? d.sectors.flatMap((s) => s.polygon)
}

/** Outer border of the district: edges that belong to a single sector. */
export function districtOutline(d: CityDistrict): Coord[][] {
  if (d.polygon) return [[...d.polygon, d.polygon[0]]]
  const edges = new Map<string, { a: Coord; b: Coord; count: number }>()
  d.sectors.forEach((s) => {
    s.polygon.forEach((a, i) => {
      const b = s.polygon[(i + 1) % s.polygon.length]
      const ka = a.join(',')
      const kb = b.join(',')
      const key = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`
      const edge = edges.get(key)
      if (edge) edge.count += 1
      else edges.set(key, { a, b, count: 1 })
    })
  })
  return [...edges.values()].filter((e) => e.count === 1).map((e) => [e.a, e.b])
}

/** Visual centre of the district (bounding-box centre), for its badge. */
export function districtCenter(d: CityDistrict): Coord {
  if (d.badgeAt) return d.badgeAt
  const pts = districtPoints(d)
  const lats = pts.map((p) => p[0])
  const lngs = pts.map((p) => p[1])
  return [
    (Math.min(...lats) + Math.max(...lats)) / 2,
    (Math.min(...lngs) + Math.max(...lngs)) / 2,
  ]
}
